import clsx from 'clsx'
import React from 'react'

import SvgCheck from '@Assets/SVG/Check'
import SvgPlus from '@Assets/SVG/Plus'
import { BuyButtonV2 } from '@Components/BuyButton/v2'
import { Chip } from '@Components/Chips/Chip/Chip'
import { getPersonalizationTranslationHeader } from '@Components/Personalization/Modals/Shared'
import { LineItemPersonalization } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/Components/LineItemPersonalization'
import { AddToCartPromptSuccessTacticFragmentFragment } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './UpgradeWith.module.scss'

type Tactic = Extract<AddToCartPromptSuccessTacticFragmentFragment, { __typename: 'TacticUpgradeWith' }>

export const UpgradeWith = ({
  parentLineItemID,
  tactic,
}: {
  parentLineItemID?: string
  tactic: Tactic
}) => {
  const { t } = useTranslation()
  const [addedToCart, setAddedToCart] = React.useState<string[]>([])
  const campaignOverrideV1 = useSplitTest('campaign_override_v1', 'b')

  if (!tactic.items || tactic.items.length === 0) {
    return null
  }

  return (
    <div className={clsx(styles.addToCartPromptSuccess, 'fullwidth', campaignOverrideV1 && styles.campaignOverride)}>
      {campaignOverrideV1 && <Chip color="summer_sale_small" />}
      <div className={styles.title}>
        {t('products:WB_UPGRADE_WITH')}
      </div>
      <div className={styles.wrapper}>
        {tactic.items.map((item) => {
          if (!item || !item.product) {
            return null
          }

          const productName = item.product.type === 'personalizations'
            ? t(getPersonalizationTranslationHeader(item.product as any))
            : item.product.name

          return (
            <LineItemPersonalization
              key={`upgrade_with_${item.product.id}`}
              action={(
                <BuyButtonV2
                  className={clsx(styles.buyButton, addedToCart.includes(item.product.id) && styles.success)}
                  color="black"
                  disabled={addedToCart.includes(item.product.id)}
                  onSuccess={() => {
                    setAddedToCart((prev) => {
                      if (prev.includes(item.product!.id)) {
                        return prev
                      }

                      return [...prev, item.product!.id]
                    })
                  }}
                  portal
                  preventDefault
                  productID={item.product!.id}
                  products={[
                    {
                      quantity: 1,
                      productID: item.product!.id,
                      parentLineItemID: item.product.type === 'personalizations' && parentLineItemID ? parentLineItemID : undefined,
                    },
                  ]}
                  sourceID="add_to_cart_success_prompt_upgrade_with"
                  stopPropagation
                  useCache={false}
                  withoutSuccessPrompt
                >
                  {(addedToCart.includes(item.product!.id)
                    ? <SvgCheck style={{ width: '10px', height: '10px' }} />
                    : <SvgPlus style={{ width: '16px', height: '16px' }} />
                  )}
                </BuyButtonV2>
              )}
              discountPrice={item.product.masterVariant?.price?.discount?.format}
              imageUrl={item.product.primaryAsset?.url || undefined}
              isFree={item.product.masterVariant?.price?.discount?.value === 0 || item.product.masterVariant?.price?.value === 0}
              isPowerStep
              onAdd={(e) => {
                e.stopPropagation()
                e.preventDefault()
                e.currentTarget?.querySelector('button')?.click()
              }}
              originalPrice={item.product.masterVariant?.price?.format}
              showCheckBox={false}
              title={productName || ''}
            />
          )
        })}
      </div>
    </div>
  )
}
